<template>
  <div class="course-preview-wrap" :class="{ lms: $config.project === 'lms' }">
    <HeroSection :course="course" :button-disabled="disabled" />
    <div class="container content ">
      <div class="row">
        <div :class="$config.project === 'site' ? 'offset-1 col col-10' : 'col col-12'">
          <Breadcrumb
            v-if="$config.project === 'site'"
            class="breadcrumb"
            :breadcrumb="[
              { name: 'Courses', slug: '/courses/' },
              { name: course.name, slug: '' }
            ]"
          />
          <Overview :overview="overview" class="overview tracking-section" data-event="course_overview.scroll" />
          <Skills :skills="skills" class="skills-learn tracking-section" data-event="what_youll_learn.scroll" />
          <Curriculum
            class="curriculum tracking-section"
            :sections="course.sections"
            :course-free="course.free"
            :paid-access="course.paidAccess"
            :unlocked="Boolean(course.unlocked)"
            data-event="curriculum.scroll"
          />
          <Reviews v-if="course.reviews" :reviews="{ courseId: course.id, ...course.reviews }" />
        </div>
      </div>
    </div>
    <div
      v-if="course.instructor.quote"
      :class="[[$config.project], { locked: course.status === 'locked' && !course.unlocked && $config.project === 'lms' }]"
      class="instructor-sign tracking-section"
      data-event="instructor_quote.scroll"
    >
      <img
        loading="lazy"
        class="instructor-image"
        :src="`${$config.imagesUrl}/courses/${course.secondaryBigImage}`"
        :alt="course.instructor.name"
      >
      <div class="container">
        <div class="row">
          <div class="col col-5 offset-1">
            <RoundedQuotesBig class="quotes-big" />
            <p class="quote-content">
              “{{ course.instructor.quote }}”
            </p>
            <p class="h5">
              <a v-if="$config.platform === 'ds'" class="text-link" :href="`${$config.siteUrl}/instructors/${quoteInstructorSlug}/`">{{ quoteInstructorName }}</a>
              <span v-else>{{ quoteInstructorName }}</span>
            </p>
            <p class="body-1">
              {{ quoteInstructorSignature }}
              <template v-if="course.instructor.signatureCompany">
                <Component :is="require(`../../assets/svg/signatures/company-${course.instructor.signatureCompany}.svg?vue-component`)" class="signature-company" />
              </template>
            </p>
          </div>
        </div>
      </div>
    </div>
    <div v-if="course.similar.length" class="container similar-courses-wrapper tracking-section" data-event="courses_you_may_like.scroll">
      <SimilarCourses :courses="course.similar" title="Courses You May Like" />
    </div>
  </div>
</template>

<script>
import global from '../../mixins/global';
import HeroSection from './HeroSection';
import Breadcrumb from './Breadcrumb';
import Overview from './Overview';
import Skills from './Skills';
import Curriculum from './Curriculum';
import SimilarCourses from './SimilarCourses';
import Reviews from './Reviews';
import RoundedQuotesBig from '../../assets/svg/rounded-quotes-big.svg?vue-component';

export default {
  components: {
    Breadcrumb, Overview, Skills, Curriculum, SimilarCourses, HeroSection, Reviews, RoundedQuotesBig,
  },
  mixins: [global],
  props: {
    course: { type: Object, required: true },
    disabled: { type: Boolean, required: false },
  },
  head() {
    const metaTitle = `${this.course.info.metaTitle}`;

    if (this.$config.project === 'site') {
      const linkedData = this.$linkedDataLoad([
        { key: 'WebSite' },
        { key: 'WebPage', values: { ...this.course.info } },
        {
          key: 'Course',
          values: {
            name: metaTitle,
            description: this.course.info.metaDescription,
            hasCourseInstance: [{
              '@type': 'CourseInstance',
              instructor: {
                '@type': 'Person',
                name: this.course.instructor.name,
              },
              courseMode: 'online',
              accessMode: 'visual',
              courseWorkload: `PT${this.course.totalHours}H`,
              educationalCredentialAwarded: `Certificate of completion - ${this.course.name}`,
              url: `${this.$config.appUrl}/courses/${this.course.slug}/`,
            }],
            offers: [{
              '@type': 'Offer',
              category: 'Paid',
              priceCurrency: 'USD',
              price: 36.00,
            }],
          },
        },
      ]);
      const link = [];

      if (this.canonicalURL) {
        link.push({ rel: 'canonical', href: this.canonicalURL() });
      }

      return {
        titleTemplate: `${metaTitle}%s`,
        link,
        meta: this.metaProps({ ...this.course.info, metaImage: `og-${this.course.slug}${this.$store.state.campaign === 'campaign-chat-gpt' && this.course.slug === 'intro-to-chatgpt-and-generative-ai' ? '-campaign' : ''}.png` }),
        script: [linkedData],
      };
    }

    return { titleTemplate: `${metaTitle}%s` };
  },
  computed: {
    overview() {
      return {
        description: this.course.description,
        hours: this.course.totalHours,
        lessons: this.course.lessons,
        practices: this.course.practicalTasks,
      };
    },
    skills() {
      return {
        learn: this.course.learn,
        tags: this.course.tags,
      };
    },
    quoteInstructorSlug() {
      if (this.$route.params.course === 'data-driven-business-growth') {
        return 'tina-huang';
      }
      if (this.$route.params.course === 'learn-machine-learning-process-a-z') {
        return 'ken-jee';
      }
      if (this.$route.params.course === 'advanced-sql') {
        return 'vladimir-saev';
      }

      return this.course.instructor.slug;
    },
    quoteInstructorName() {
      if (this.$route.params.course === 'data-driven-business-growth') {
        return 'Tina Huang';
      }
      if (this.$route.params.course === 'learn-machine-learning-process-a-z') {
        return 'Ken Jee';
      }
      if (this.$route.params.course === 'advanced-sql') {
        return 'Vladimir Saev';
      }

      return this.course.instructor.name;
    },
    quoteInstructorSignature() {
      if (this.$route.params.course === 'data-driven-business-growth') {
        return '260K Subscribers on Youtube';
      }
      if (this.$route.params.course === 'learn-machine-learning-process-a-z') {
        return '220K Subscribers on Youtube';
      }
      if (this.$route.params.course === 'advanced-sql') {
        return 'Worked at PaySafe Group';
      }

      return this.course.instructor.signature;
    },
  },
};
</script>

<style lang="scss">
.course-preview-wrap {
  padding-bottom: var(--spacing-55);
  background: var(--main-light-shade-1);

  .overview,
  .skills-learn,
  .curriculum {
    margin-top: var(--spacing-60);
    margin-bottom: var(--spacing-140);
  }

  .instructor-sign {
    padding: 96px 0;
    color: var(--main-light);
    position: relative;
    min-height: 500px;

    &.locked {
      background-color: var(--main-dark);

      .instructor-image {
        mix-blend-mode: luminosity;
      }
    }

    .instructor-image {
      position: absolute;
      height: 100%;
      width: 100%;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      object-fit: cover;
    }

    .body-1 {
      display: flex;
      align-items: center;
      font-weight: 400;
      color: var(--main-light-shade-4);
      margin: 0;

      svg {
        margin-left: var(--spacing-8);
      }
    }

    .quote-content {
      font-weight: normal;
      font-size: 20px;
      line-height: 1.5;
      margin: 0 0 var(--spacing-36);
    }

    .text-link {
      color: inherit;

      &:hover {
        color: var(--main-light-shade-4);
      }
    }
  }

  .similar-courses-wrapper .similar-courses {
    .slider-header {
      .h3 {
        margin-bottom: 0;
        font-size: 21px;
        line-height: 26px;
      }
    }
  }

  &.lms {
    .overview,
    .skills-learn,
    .curriculum {
      margin-bottom: var(--spacing-70);
    }
  }

  @include bp($bp-laptop) {
    .hero-section-layout.lms .wall {
      background-position: 60% center;
    }

    .instructor-sign .quote-content {
      font-size: 18px;
    }
  }

  @include bp($bp-tablet) {
    .hero-section-layout.site {
      .wall {
        background-size: cover;
        background-position: 82%;

        &:after {
          bottom: 0;
          top: auto;
        }
      }
    }
  }

  @include bp($bp-mobile) {
    .overview,
    .skills-learn,
    .curriculum,
    .similar-courses {
      margin-top: var(--spacing-80);
      margin-bottom: var(--spacing-80);

      .skills-box {
        .h4 {
          font-size: 21px;
        }
      }
    }

    .instructor-sign {
      background-color: var(--main-dark);
      padding: 0;

      .instructor-image {
        background-position: 98% 0;
        background-size: 180%;
        position: relative;
        height: 432px;
        object-position: right;

        &:before {
          content: '';
          background: linear-gradient(-180deg, var(--main-dark) 0%, rgba(0, 12, 31, 0) 100%);
          height: 150px;
          display: block;
          position: absolute;
          left: 0;
          right: 0;
          bottom: -1px;
          z-index: 0;
          transform: rotate(-180deg);
        }
      }

      .container {
        position: relative;
        padding-bottom: var(--spacing-80);

        &:after {
          content: '';
          position: absolute;
          width: 100%;
          height: 127.91px;
          left: 0;
          top: -128px;
          background: linear-gradient(180deg, #000C1F 0%, rgba(0, 12, 31, 0) 100%);
          transform: rotate(-180deg);
        }
      }

      .col svg.quotes-big {
        display: none;
      }

      .body-1 {
        font-size: 16px;
      }

      .quote-content {
        font-size: 18px;
      }

      .h5:first-child {
        font-weight: 500;
        font-size: 20px;
      }
    }

    .similar-courses-wrapper .similar-courses {
      .slider-header {
        justify-content: center;
        margin-bottom: var(--spacing-36);
      }
    }

    &.lms .similar-courses-wrapper .similar-courses {
      margin-top: 64px;
    }
  }

  @include bp($bp-phone) {
    .instructor-sign {
      .instructor-image {
        object-position: 80%;
        background-position: 78% 0;
        background-size: 250%;
        height: 300px;
      }

      .container .row .col h4 {
        font-size: 18px;
      }
    }
  }
}
</style>
